import type { Agent, ChatMessage, ChatSession } from '../types';
import type { ChatExecutionMode } from '../hooks/useChatAgent';

export type ChatScreenInitialState = {
  initialMessage?: string;
  initialFile?: File | null;
  initialMode?: ChatExecutionMode;
  initialStartRealtimeVoice?: boolean;
  initialSession?: ChatSession;
  initialAgent?: Agent | null;
};

export const getInitialMessageSessionId = (state: ChatScreenInitialState | null) =>
  state?.initialSession && state.initialMessage?.trim() ? state.initialSession.id : null;

export const getMatchingInitialSession = (
  state: ChatScreenInitialState | null,
  routeSessionId?: string
) => {
  const session = state?.initialSession;
  if (
    session &&
    routeSessionId &&
    (session.id === routeSessionId || session.publicId === routeSessionId) &&
    typeof session.internalId === 'number' &&
    session.agentPublicId
  ) {
    return session;
  }
  return null;
};

export const getMatchingInitialAgent = (
  state: ChatScreenInitialState | null,
  agentPublicId: string
) => {
  const agent = state?.initialAgent;
  return agent?.publicId === agentPublicId ? agent : null;
};

export const resolveRouteAgent = (
  loadedAgent: Agent | null,
  session: ChatSession,
  fallbackName: string,
  fallbackDescription: string
): Agent =>
  loadedAgent
    ? { ...loadedAgent, id: loadedAgent.id || loadedAgent.publicId }
    : {
        id: session.agentPublicId,
        publicId: session.agentPublicId,
        name: session.agentName || fallbackName,
        description: fallbackDescription,
        avatarUrl: null,
        characteristicIds: [],
        knowledgeIds: [],
        ownerType: 'USER',
        isDefault: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

/** Session seeded by /new-chat with a first message; lets the conversation render without a loading screen. */
export const getSeededInitialSession = (
  state: ChatScreenInitialState | null,
  routeSessionId?: string
) => (getInitialMessageSessionId(state) ? getMatchingInitialSession(state, routeSessionId) : null);

export const buildPendingInitialMessage = (
  sessionId: string,
  content: string,
  file: File | null,
  fileUrl: string | null
): ChatMessage => ({
  id: `pending-initial-${sessionId}`,
  sessionId,
  role: 'user',
  content,
  attachments:
    file && fileUrl
      ? [
          {
            publicId: `pending-initial-${sessionId}-attachment`,
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            fileSize: file.size,
            fileUrl,
          },
        ]
      : [],
  timestamp: new Date(),
  status: 'sending',
});
